import { Category } from '@features/categories/pages/Category';
import { Category as CategoryType } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import * as entityActions from '@store/entity/actions';
import { NormalizedStore } from '@store/entity/entity.types';
import { entitySelectors } from '@store/entity/selectors';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import React from 'react';
import Error from 'next/error';

type CategoryPageProps = {
  id: string;
  language: string;
  url: string;
  property: Property;
  category: CategoryType | null;
};

const CategoryPage: NextPage<CategoryPageProps> = ({ property, category, language, url, id }) => {
  if (!category) {
    return <Error statusCode={404} />;
  }

  const pageTitle = property?.propertyTitle
    ? `${category.name} | ${property.propertyTitle}`
    : category.name;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: category.description,
            locale: language,
            url,
            images: [
              {
                url: category.image ? category.image.url : property?.socialFallbackImage?.url,
              },
            ],
          },
        }}
      />
      <Category id={id} key={id} />
    </>
  );
};

type CategoryPageInitialProps = {
  property: Property;
  store: NormalizedStore;
} & NextPageContext &
  Pick<CategoryPageProps, 'language' | 'url'>;

CategoryPage.getInitialProps = async ({
  store,
  property,
  language,
  query,
  url,
}: CategoryPageInitialProps) => {
  await Promise.all([
    entityActions.fetchCategory(property.id, query.id, language)(store.dispatch),
    entityActions.fetchCategoryProducts(property.id, query.id, language, {
      perPage: 0,
    })(store.dispatch),
  ]);
  const category = entitySelectors.getCategory(store.getState(), query.id);

  return {
    id: query.id,
    category,
    language,
    url,
    property,
  } as CategoryPageProps;
};

export default CategoryPage;
